import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { IKO_GROUPS, ikoYears, MONTH_KEYS } from '@/lib/iko';
import { Award, Plus, Trash2, ShieldCheck, ShieldOff } from 'lucide-react';

const emptyCert = () => ({ cert: '', school: '', location: '', month: null, year: null, card_number: '' });

export const IkoCertEditor = ({ certified, certs, onChange }) => {
  const { t } = useTranslation();
  const years = ikoYears();

  const setCertified = (val) => {
    if (val) {
      onChange({ certified: true, certs: certs.length ? certs : [emptyCert()] });
    } else {
      onChange({ certified: false, certs: [] });
    }
  };

  const updateEntry = (idx, patch) => {
    onChange({ certified, certs: certs.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  };
  const addEntry = () => onChange({ certified: true, certs: [...certs, emptyCert()] });
  const removeEntry = (idx) => {
    const next = certs.filter((_, i) => i !== idx);
    onChange({ certified: next.length > 0, certs: next });
  };

  const choiceBtn = (active, onClick, Icon, label, testid) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
        active
          ? 'bg-[#00B4D8]/15 border-[#00B4D8]/50 text-[#48CAE4]'
          : 'bg-white/[0.03] border-white/10 text-muted-foreground hover:text-white hover:border-white/20'
      }`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div data-testid="iko-cert-editor">
      <div className="flex items-center gap-2.5">
        {choiceBtn(certified === true, () => setCertified(true), ShieldCheck, t('iko.hasYes'), 'iko-has-yes')}
        {choiceBtn(certified === false, () => setCertified(false), ShieldOff, t('iko.hasNo'), 'iko-has-no')}
      </div>

      {certified && (
        <div className="mt-4 space-y-3" data-testid="iko-entries">
          {certs.map((entry, idx) => (
            <div key={idx} className="rounded-xl border border-white/10 bg-[#0A0A0A] p-4" data-testid={`iko-entry-${idx}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="label-eyebrow text-[#48CAE4] flex items-center gap-1.5">
                  <Award className="w-3.5 h-3.5" /> {t('iko.certificate')} {certs.length > 1 ? `#${idx + 1}` : ''}
                </span>
                {certs.length > 1 && (
                  <button type="button" onClick={() => removeEntry(idx)} className="text-muted-foreground hover:text-[#FF6B35] transition-colors" data-testid={`iko-remove-${idx}`} aria-label={t('iko.remove')}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs">{t('iko.certLabel')}</Label>
                  <Select value={entry.cert || undefined} onValueChange={(v) => updateEntry(idx, { cert: v })}>
                    <SelectTrigger className="mt-1.5 bg-[#12161B] border-white/15" data-testid={`iko-cert-select-${idx}`}>
                      <SelectValue placeholder={t('iko.certPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-72">
                      {IKO_GROUPS.map((g) => (
                        <SelectGroup key={g.key}>
                          <SelectLabel className="text-[#48CAE4]">{t(`iko.groups.${g.key}`)}</SelectLabel>
                          {g.certs.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('iko.school')}</Label>
                    <Input
                      value={entry.school || ''}
                      onChange={(e) => updateEntry(idx, { school: e.target.value })}
                      placeholder={t('iko.schoolPlaceholder')}
                      className="mt-1.5 bg-[#12161B] border-white/15"
                      data-testid={`iko-school-${idx}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('iko.location')}</Label>
                    <Input
                      value={entry.location || ''}
                      onChange={(e) => updateEntry(idx, { location: e.target.value })}
                      placeholder={t('iko.locationPlaceholder')}
                      className="mt-1.5 bg-[#12161B] border-white/15"
                      data-testid={`iko-location-${idx}`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('iko.month')}</Label>
                    <Select
                      value={entry.month ? String(entry.month) : undefined}
                      onValueChange={(v) => updateEntry(idx, { month: parseInt(v, 10) })}
                    >
                      <SelectTrigger className="mt-1.5 bg-[#12161B] border-white/15" data-testid={`iko-month-${idx}`}>
                        <SelectValue placeholder={t('iko.month')} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-72">
                        {MONTH_KEYS.map((m, i) => (
                          <SelectItem key={m} value={String(i + 1)}>{t(`iko.months.${m}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">{t('iko.year')}</Label>
                    <Select
                      value={entry.year ? String(entry.year) : undefined}
                      onValueChange={(v) => updateEntry(idx, { year: parseInt(v, 10) })}
                    >
                      <SelectTrigger className="mt-1.5 bg-[#12161B] border-white/15" data-testid={`iko-year-${idx}`}>
                        <SelectValue placeholder={t('iko.year')} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-72">
                        {years.map((y) => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs">{t('iko.cardNumber')} <span className="text-muted-foreground font-normal">· {t('iko.optional')}</span></Label>
                  <Input
                    value={entry.card_number || ''}
                    onChange={(e) => updateEntry(idx, { card_number: e.target.value })}
                    placeholder={t('iko.cardPlaceholder')}
                    className="mt-1.5 bg-[#12161B] border-white/15 font-data"
                    data-testid={`iko-card-${idx}`}
                  />
                </div>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={addEntry}
            className="w-full border-dashed border-white/20 bg-white/[0.02] hover:bg-white/5"
            data-testid="iko-add-cert"
          >
            <Plus className="w-4 h-4 mr-2" /> {t('iko.addCertificate')}
          </Button>
        </div>
      )}
    </div>
  );
};
